/**
 * Collection "Load more" — appends next page products via Section Rendering API.
 * Preserves filter/sort query params; updates history for back-button support.
 */
class LoadMoreProducts extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.btn = this.querySelector('[data-load-more-btn]');
    this.label = this.querySelector('[data-load-more-label]');
    this.spinner = this.querySelector('[data-load-more-spinner]');
    this.status = this.querySelector('[data-load-more-status]');
    this.sectionId = this.dataset.sectionId || '';
    this.nextUrl = this.dataset.nextUrl || '';
    this.loading = false;

    /* Ensure spinner starts hidden (CSS + attribute) */
    this.setLoading(false);

    if (this.btn) {
      this._onClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.loadNext();
      };
      this.btn.addEventListener('click', this._onClick);
    }
  }

  disconnectedCallback() {
    if (this.btn && this._onClick) {
      this.btn.removeEventListener('click', this._onClick);
    }
    this._bound = false;
  }

  get grid() {
    return document.getElementById('product-grid');
  }

  /**
   * Build section fetch URL from next page link + section_id.
   * Merges current location filters if nextUrl is relative/partial.
   */
  buildFetchUrl(nextUrl) {
    const url = new URL(nextUrl, window.location.href);

    /* Keep any filter/sort params from current page that next URL omitted */
    const current = new URL(window.location.href);
    current.searchParams.forEach((value, key) => {
      if (key === 'section_id' || key === 'page') return;
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });

    url.searchParams.set('section_id', this.sectionId);
    return url.pathname + url.search;
  }

  buildHistoryUrl(nextUrl) {
    const url = new URL(nextUrl, window.location.href);
    const current = new URL(window.location.href);
    current.searchParams.forEach((value, key) => {
      if (key === 'section_id' || key === 'page') return;
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });
    url.searchParams.delete('section_id');
    const qs = url.searchParams.toString();
    return url.pathname + (qs ? `?${qs}` : '');
  }

  setLoading(isLoading) {
    this.loading = isLoading;
    if (this.btn) {
      this.btn.disabled = !!isLoading;
      this.btn.classList.toggle('is-loading', !!isLoading);
      this.btn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      /* Never use Dawn .button.loading — it hides label and forces spinner layout */
      this.btn.classList.remove('loading');
    }
    if (this.spinner) {
      if (isLoading) {
        this.spinner.removeAttribute('hidden');
        this.spinner.setAttribute('aria-hidden', 'false');
      } else {
        this.spinner.setAttribute('hidden', '');
        this.spinner.setAttribute('aria-hidden', 'true');
      }
    }
  }

  async loadNext() {
    if (this.loading) return;

    /* Re-read in case facets re-rendered the node */
    this.nextUrl = this.dataset.nextUrl || this.nextUrl || '';
    this.sectionId =
      this.dataset.sectionId ||
      (this.grid && this.grid.dataset.id) ||
      this.sectionId ||
      '';

    if (!this.nextUrl || !this.grid || !this.sectionId) {
      if (this.status) {
        this.status.textContent = 'No more products to load.';
      }
      if (this.btn) this.btn.hidden = true;
      return;
    }

    this.setLoading(true);
    if (this.status) this.status.textContent = 'Loading more products…';

    try {
      const fetchUrl = this.buildFetchUrl(this.nextUrl);
      const response = await fetch(fetchUrl, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) {
        throw new Error('Load more request failed: ' + response.status);
      }

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const nextGrid = doc.getElementById('product-grid');
      const nextLoadMore = doc.querySelector('load-more-products');

      if (!nextGrid) {
        throw new Error('Product grid not found in response');
      }

      const items = Array.from(nextGrid.querySelectorAll('.grid__item'));
      if (!items.length) {
        this.nextUrl = '';
        this.removeAttribute('data-next-url');
        if (this.btn) this.btn.hidden = true;
        if (this.status) this.status.textContent = 'All products loaded.';
        return;
      }

      const fragment = document.createDocumentFragment();
      items.forEach((item) => fragment.appendChild(item));
      this.grid.appendChild(fragment);

      if (this.status) {
        this.status.textContent =
          items.length === 1
            ? '1 more product loaded.'
            : items.length + ' more products loaded.';
      }

      const historyUrl = this.buildHistoryUrl(this.nextUrl);
      const searchParams = new URL(historyUrl, window.location.origin).searchParams.toString();
      history.pushState({ searchParams }, '', historyUrl);

      if (typeof FacetFiltersForm !== 'undefined') {
        FacetFiltersForm.searchParamsPrev = searchParams;
      }

      const newNext =
        (nextLoadMore && (nextLoadMore.getAttribute('data-next-url') || nextLoadMore.dataset.nextUrl)) ||
        '';
      if (newNext) {
        this.nextUrl = newNext;
        this.setAttribute('data-next-url', newNext);
        if (nextLoadMore.dataset.currentPage) {
          this.dataset.currentPage = nextLoadMore.dataset.currentPage;
        }
      } else {
        this.nextUrl = '';
        this.removeAttribute('data-next-url');
        if (this.btn) this.btn.hidden = true;
        if (this.status) this.status.textContent = 'All products loaded.';
      }

      const currentPag = this.closest('.collection')
        ? this.closest('.collection').querySelector('[data-pagination-fallback]')
        : document.querySelector('[data-pagination-fallback]');
      const nextPag = doc.querySelector('[data-pagination-fallback]');
      if (currentPag && nextPag) {
        currentPag.innerHTML = nextPag.innerHTML;
      }

      if (window.ShopifyWishlist) {
        window.ShopifyWishlist.bindToggles(this.grid);
        window.ShopifyWishlist.updateButtons(this.grid);
      }
    } catch (err) {
      console.error('[load-more]', err);
      if (this.status) {
        this.status.textContent = 'Could not load more products. Please try again.';
      }
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get('load-more-products')) {
  customElements.define('load-more-products', LoadMoreProducts);
}

/* Re-bind after FacetFiltersForm replaces ProductGridContainer HTML */
(function () {
  const reinit = () => {
    document.querySelectorAll('load-more-products').forEach((el) => {
      if (typeof el.connectedCallback === 'function' && !el._bound) {
        el.connectedCallback();
      }
    });
  };

  /* Facets replace innerHTML — custom elements upgrade automatically; ensure listeners */
  const origRender =
    typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.renderProductGridContainer;
  if (origRender && !FacetFiltersForm.__loadMorePatched) {
    FacetFiltersForm.__loadMorePatched = true;
    FacetFiltersForm.renderProductGridContainer = function (html) {
      origRender.call(FacetFiltersForm, html);
      /* Microtask: allow custom element upgrade */
      Promise.resolve().then(reinit);
    };
  }

  if (window.__loadMoreHistoryBound) return;
  window.__loadMoreHistoryBound = true;

  window.addEventListener('popstate', (event) => {
    if (typeof FacetFiltersForm !== 'undefined') return;
    const grid = document.getElementById('product-grid');
    if (!grid || !grid.dataset.id) return;

    const searchParams =
      (event.state && event.state.searchParams) ||
      window.location.search.replace(/^\?/, '');
    const url =
      window.location.pathname +
      '?section_id=' +
      encodeURIComponent(grid.dataset.id) +
      (searchParams ? '&' + searchParams : '');

    fetch(url, { credentials: 'same-origin' })
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const container = document.getElementById('ProductGridContainer');
        const nextContainer = doc.getElementById('ProductGridContainer');
        if (container && nextContainer) {
          container.innerHTML = nextContainer.innerHTML;
          if (window.ShopifyWishlist) {
            window.ShopifyWishlist.bindToggles(container);
            window.ShopifyWishlist.updateButtons(container);
          }
        }
      })
      .catch(() => {
        window.location.reload();
      });
  });
})();
